import { useCallback, useEffect, useRef, useState } from 'react';
import {
    createDistributionGroup,
    deleteDistributionGroup,
    getDistributionGroup,
    listDistributionGroups,
    updateDistributionGroup,
} from '../../services/distribution/distribution.service';
import type { DistributionGroupDetail, DistributionGroupSummary } from '../../services/distribution/distribution.types';
import {
    deleteGroupRecipient,
    getDistributionErrorMessage,
    hydrateGroupRecipients,
    saveGroupRecipients,
} from './distributionGroupRecipients';
import type { DistributionGroupFormValues } from './distributionGroupRecipients';

function normalizeGroup(value: unknown): DistributionGroupSummary {
    if (!value || typeof value !== 'object' ||
        !('id' in value) || typeof value.id !== 'number' ||
        !('profileName' in value) || typeof value.profileName !== 'string') {
        throw new Error('Invalid distribution group response');
    }
    return {
        id: value.id,
        profileName: value.profileName,
        recipientCount: 'recipientCount' in value && typeof value.recipientCount === 'number' ? value.recipientCount : 0,
        activeRecipientCount: 'activeRecipientCount' in value && typeof value.activeRecipientCount === 'number' ? value.activeRecipientCount : 0,
        hasEmailRecipients: 'hasEmailRecipients' in value && value.hasEmailRecipients === true,
        hasSmsRecipients: 'hasSmsRecipients' in value && value.hasSmsRecipients === true,
        hasBureauRecipients: 'hasBureauRecipients' in value && value.hasBureauRecipients === true,
        createdAt: 'createdAt' in value && typeof value.createdAt === 'string' ? value.createdAt : '',
        updatedAt: 'updatedAt' in value && typeof value.updatedAt === 'string' ? value.updatedAt : undefined,
    };
}

function normalizeGroups(data: unknown): DistributionGroupSummary[] {
    if (Array.isArray(data)) return data.map(normalizeGroup);
    if (data && typeof data === 'object') return Object.values(data).map(normalizeGroup);
    throw new Error('Invalid distribution group list response');
}

export function useDistributionGroups() {
    const [groups, setGroups] = useState<DistributionGroupSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const requestId = useRef(0);

    const refresh = useCallback(async (suppressLoading = false): Promise<string | null> => {
        const currentRequest = ++requestId.current;
        if (!suppressLoading) setLoading(true);
        try {
            const response = await listDistributionGroups();
            if (!response.success) throw new Error(response.error?.message || 'Failed to load distribution groups');
            const nextGroups = normalizeGroups(response.data);
            if (currentRequest === requestId.current) setGroups(nextGroups);
            return null;
        } catch (error: unknown) {
            const message = getDistributionErrorMessage(error, 'Failed to load distribution groups');
            if (currentRequest === requestId.current) setError(message);
            return message;
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
        return () => { requestId.current += 1; };
    }, [refresh]);

    const loadForEdit = async (id: number): Promise<DistributionGroupDetail | null> => {
        setError(null);
        try {
            const response = await getDistributionGroup(id);
            if (!response.success || !response.data) {
                throw new Error(response.error?.message || 'Failed to load distribution group details');
            }
            return await hydrateGroupRecipients(response.data);
        } catch (error: unknown) {
            setError(getDistributionErrorMessage(error, 'Failed to load distribution group details'));
            return null;
        }
    };

    const save = async (values: DistributionGroupFormValues, groupId?: number): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            const response = groupId !== undefined
                ? await updateDistributionGroup(groupId, { id: groupId, profileName: values.profileName })
                : await createDistributionGroup({ profileName: values.profileName });
            if (!response.success || !response.data) {
                throw new Error(response.error?.message || 'Failed to save distribution group');
            }
            const savedGroup = normalizeGroup(response.data);
            setGroups(previous => groupId !== undefined
                ? previous.map(group => group.id === groupId ? savedGroup : group)
                : [savedGroup, ...previous]);

            const recipients = values.recipients ?? [];
            const failures = await saveGroupRecipients(savedGroup.id, recipients, groupId !== undefined);
            const refreshError = recipients.length ? await refresh(true) : null;
            if (refreshError) failures.push(refreshError);
            if (failures.length) {
                setError(failures.join('\n'));
            } else {
                setSuccess(groupId !== undefined ? 'Distribution group updated' : 'Distribution group created');
            }
            // The group is persisted even if some recipient operations failed.
            return true;
        } catch (error: unknown) {
            setError(getDistributionErrorMessage(error, 'Failed to save distribution group'));
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id: number) => {
        setError(null);
        setSuccess(null);
        try {
            const response = await deleteDistributionGroup(id);
            if (!response.success) throw new Error(response.error?.message || 'Failed to delete distribution group');
            setGroups(previous => previous.filter(group => group.id !== id));
            setSuccess('Distribution group deleted');
        } catch (error: unknown) {
            setError(getDistributionErrorMessage(error, 'Failed to delete distribution group'));
        }
    };

    const removeRecipient = async (groupId: number, recipientId: number) => {
        setError(null);
        try {
            await deleteGroupRecipient(groupId, recipientId);
        } catch (error: unknown) {
            setError(getDistributionErrorMessage(error, 'Failed to delete recipient'));
            throw error;
        }
        await refresh(true);
    };

    return {
        groups, loading, submitting, error, success, loadForEdit, save, remove, removeRecipient,
        clearError: () => setError(null),
        clearSuccess: () => setSuccess(null),
    };
}
